import axios, { AxiosRequestConfig, Method } from 'axios'
import cookieParser from 'cookie'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'

import { PATHS } from 'api/paths'
import { domainLocales } from 'domainLocales'
import {
  BEARER_REFRESH_TOKEN_COOKIE,
  BEARER_TOKEN_COOKIE,
  ENV,
  GA_TOKEN_COOKIE,
  LEAD_TOKEN_COOKIE,
} from 'interfaces/constants'
import { LOCALE } from 'interfaces/types/Locale'
import type { UserInfo } from 'interfaces/types/User'
import { MeasureLogHandler, MeasureOptions } from 'utils/measureTaskExecTime'

import { logApiCall } from './logs'
import { callApiNew } from '../axios'

export const locale = (ENV.NEXT_PUBLIC_DEPLOY_LOCALE || 'en-PH') as LOCALE

export const createAxiosWithInterceptor = (
  token?: string,
  tokenType?: string,
) => {
  const apiInstance = axios.create()
  apiInstance.interceptors.request.use(
    (config) => {
      token &&
        tokenType &&
        (config.headers.Authorization = `${tokenType} ${token}`)

      return config
    },
    (error) => {
      Promise.reject(error)
    },
  )

  return apiInstance
}

const fixedEncodeURI = (str: string) => {
  return encodeURI(str).replace(/%5B/g, '[').replace(/%5D/g, ']')
}

/**
 * @param options.requestFrom get source from request const requestFrom = req.headers['x-forwarded-for'] || req.headers.cookie || req.socket.remoteAddress
 */
export const callApi = async (
  url: string,
  method: Method,
  options?: AxiosRequestConfig & {
    /** const requestFrom = req.headers['x-forwarded-for'] || req.headers.cookie || req.socket.remoteAddress */
    requestFrom?: string | string[] | undefined
  },
  skipEncodeURI = false,
  measureOptions?: MeasureOptions,
) => {
  try {
    const interceptedAxios = createAxiosWithInterceptor()
    const res = await interceptedAxios({
      url: skipEncodeURI ? url : fixedEncodeURI(url),
      method,
      ...options,
      params: {
        lang: ENV.NEXT_PUBLIC_DEPLOY_LOCALE === 'tl-PH' ? 'fil' : undefined,
        ...options?.params,
      },
      timeout: 30000,
    })

    if (res) {
      measureOptions &&
        MeasureLogHandler.attachMetadataByTask({
          id: measureOptions.id,
          task: measureOptions.task,
          metadata: {
            requestUrl: `${res.config.baseURL}${res.config.url}`,
          },
        })

      return res.data
    }
  } catch (err) {
    logApiCall(null, url, method, options?.requestFrom, err)
    return null
  }
  return null
}

/**
 * @param options.requestFrom get source from request const requestFrom = req.headers['x-forwarded-for'] || req.headers.cookie || req.socket.remoteAddress
 */
export const callApiWithAuth = async (
  url: string,
  method: Method,
  options?: AxiosRequestConfig & {
    requestFrom?: string | string[] | undefined
    noAuthFallback?: boolean
    allowError?: boolean
  },
  reqCookie?: string,
) => {
  try {
    const parsedCookie = cookieParser.parse(reqCookie || '')
    const token = parsedCookie[BEARER_TOKEN_COOKIE]
    const cookie = token || Cookies.get(BEARER_TOKEN_COOKIE)
    if (cookie && cookie.length) {
      const authAxios = createAxiosWithInterceptor(cookie, 'Bearer')
      const res = await authAxios({
        url,
        method,
        ...options,
        params: {
          ...options?.params,
          lang: ENV.NEXT_PUBLIC_DEPLOY_LOCALE === 'tl-PH' ? 'fil' : undefined,
        },
        timeout: 30000,
      })

      if (res) {
        return res.data
      }
    } else if (options?.noAuthFallback) {
      return callApi(url, method, options)
    }
  } catch (err) {
    logApiCall(null, url, method, options?.requestFrom, err)
    if (options?.noAuthFallback) {
      return callApi(url, method, options)
    }

    if (options?.allowError) {
      throw err
    } else {
      return null
    }
  }
  return null
}

export const callApiWithLead = async (
  url: string,
  method: Method,
  options?: AxiosRequestConfig,
  reqCookie?: string,
) => {
  try {
    const parsedCookie = cookieParser.parse(reqCookie || '')
    const token = parsedCookie[LEAD_TOKEN_COOKIE]
    let cookie = token || Cookies.get(LEAD_TOKEN_COOKIE)
    const ga = parsedCookie[GA_TOKEN_COOKIE] || Cookies.get(GA_TOKEN_COOKIE)

    if (!cookie) {
      const newCookie = uuidv4()
      cookie = newCookie
      Cookies.set(LEAD_TOKEN_COOKIE, newCookie)
    }

    const res = await axios({
      url: fixedEncodeURI(url),
      method,
      ...options,
      data: {
        ...options?.data,
        cookie_id: cookie,
        ga_client_id: ga || '',
      },
      timeout: 30000,
    })
    if (res) return res.data
  } catch (err) {
    logApiCall(null, url, method, 'lead', err)
    return null
  }
  return null
}

// TODO: guess what
// let logApiCall: any = () => false
// export function setLogApiCallFn(fn: any) => {
//   logApiCall = fn
// }

export const authorize = async (): Promise<UserInfo | null> => {
  try {
    let response
    let error
    try {
      response = await callApiNew(
        PATHS.USER_PROFILE.GET_PROFILE,
        {
          method: 'POST',
        },
        'sso',
      )
    } catch (err: any) {
      error = err
      if (!response) {
        response = err.response
      }
    }
    if (response?.code === 'SSO_ERR_041' || response?._status !== 1) {
      const refreshAuthCookie = Cookies.get(BEARER_REFRESH_TOKEN_COOKIE)
      if (refreshAuthCookie) {
        const refreshResponse = await axios({
          url:
            process.env.NEXT_PUBLIC_SSO +
            'api/' +
            PATHS.USER_PROFILE.POST_USER_REFRESH,
          method: 'POST',
          timeout: 10000,
          data: {
            refresh_token: refreshAuthCookie,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })
        if (refreshResponse?.data?._data?.token) {
          Cookies.set(BEARER_TOKEN_COOKIE, refreshResponse.data._data.token, {
            domain:
              window.location.hostname === 'localhost'
                ? 'localhost'
                : `.${domainLocales[locale]}`,
            expires: refreshResponse.data._data.expire_in || undefined,
          })
          return authorize()
        }
      }
    }
    if (error) throw error
    if (response) {
      return response._data
    }
  } catch (err) {
    // logApiCall(null, PATHS.USER_PROFILE.GET_PROFILE, 'POST', undefined, err)
    console.log({ err })
    return null
  }

  return null
}

/** To make sure have the same window id until refresh the page */
const generateWindowId = uuidv4()
export const getWindowId = () => {
  return generateWindowId
}

export const callSSOApi = (
  endpoint: string,
  options: AxiosRequestConfig = {},
) => {
  const authToken = Cookies.get(BEARER_TOKEN_COOKIE)
  if (!authToken) {
    return Promise.reject(
      `[SSO API] Missing auth cookie when calling ${endpoint}`,
    )
  }
  return callApiNew(
    endpoint,
    {
      ...options,
    },
    'sso',
  )
}

export const postSSOApiFormData = (
  endpoint: string,
  data: object,
  options: AxiosRequestConfig = {},
) => {
  const bodyFormData = new FormData()
  for (const [k, v] of Object.entries(data)) {
    bodyFormData.append(k, v)
  }
  return callSSOApi(endpoint, {
    method: 'POST',
    data: bodyFormData,
    headers: { 'Content-Type': 'multipart/form-data' },
    ...options,
  })
}
